/**
 * WebHotelier Proxy Service
 * Uses Supabase Edge Functions to bypass CORS restrictions
 */

import { supabase } from '../../../services/supabaseClient';
import { resolveFunctionsHttpError } from '../../../services/edgeFunctionErrors';
import {
  PropertyInfo,
  AvailabilityRequest,
  AvailabilityResponse,
  WebHotelierConfig,
  WebHotelierIntegrationStatus,
  BookingDetails,
  BookingSearchFilters,
  BookingSearchResult,
  CancellationResponse,
  BookingPullResponse,
  RoomListResponse,
  RoomTypeInfo,
  RateListResponse,
  RateDetails,
  AvailabilityUpdate,
  AvailabilityUpdateResponse,
  CalendarRequest,
  CalendarResponse,
  BreakdownResponse,
  RateUpdateRequest,
  RateUpdateResponse,
} from '../../../types/webHotelier';
import {
  createSuccessResult,
  createErrorResult,
  type ErrorHandlingResult,
  ERROR_CODES,
} from '../../../utils/errorHandling';
import {
  assertPropertyAccess,
  PropertyAuthorizationError,
} from '../../../services/propertyAuthorization';

function authorizationFailure(error: unknown): ErrorHandlingResult<never> {
  return createErrorResult({
    code:
      error instanceof PropertyAuthorizationError
        ? ERROR_CODES.FORBIDDEN
        : ERROR_CODES.INTERNAL_ERROR,
    message:
      error instanceof Error ? error.message : 'Authorization check failed',
    severity: 'high',
    retryable: false,
  });
}

async function getAuthHeaders(): Promise<{
  Authorization: string;
  apikey: string;
}> {
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('No active session — please sign in to sync WebHotelier');
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    apikey: anonKey,
  };
}

export class WebHotelierProxyService {
  private config: WebHotelierConfig;
  private propertyId?: string;
  private propertyInfoCache: PropertyInfo | null = null;
  private propertyInfoCacheTime: number = 0;
  private roomTypesCache: RoomListResponse | null = null;
  private roomTypesCacheTime: number = 0;
  private ratesCache: RateListResponse | null = null;
  private ratesCacheTime: number = 0;
  private availabilityCache: Map<
    string,
    { data: AvailabilityResponse; time: number }
  > = new Map();
  // Pending request deduplication - prevents concurrent duplicate API calls
  private pendingPropertyInfo: Promise<
    ErrorHandlingResult<PropertyInfo>
  > | null = null;
  private pendingRoomTypes: Promise<
    ErrorHandlingResult<RoomListResponse>
  > | null = null;
  private pendingRates: Promise<ErrorHandlingResult<RateListResponse>> | null =
    null;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly AVAILABILITY_CACHE_TTL = 2 * 60 * 1000; // 2 minutes for availability (shorter TTL)
  private static readonly INVOKE_TIMEOUT = 15000; // 15 second timeout for Edge Function calls

  constructor(config: WebHotelierConfig, propertyId?: string) {
    this.config = config;
    this.propertyId = propertyId;
  }

  getPropertyCode(): string {
    return this.config.propertyCode;
  }

  getPropertyId(): string | undefined {
    return this.propertyId;
  }

  private getRequestBody(): Record<string, unknown> {
    // Runtime proxy calls MUST go through propertyId. The Edge Function
    // resolves credentials from Vault server-side — the browser never
    // holds or transmits the WebHotelier password for a stored
    // integration. If we got here without a propertyId, that means an
    // instance was constructed outside the setActiveProperty() flow and
    // is about to leak plaintext creds onto the wire (and into function
    // logs / observability). Fail loudly instead.
    if (!this.propertyId) {
      throw new Error(
        '[WebHotelier Proxy] propertyId is required; plaintext credentials in the request body are no longer supported. Call setActiveProperty(id, code) before issuing requests.'
      );
    }
    return { propertyId: this.propertyId };
  }

  /**
   * Direct fetch to Edge Function (faster than supabase.functions.invoke)
   * Uses AbortController for reliable timeout handling
   */
  private async invokeWithTimeout<T>(
    _functionName: string,
    body: Record<string, unknown>
  ): Promise<{ data: T | null; error: Error | null }> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const authHeaders = await getAuthHeaders();

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      WebHotelierProxyService.INVOKE_TIMEOUT
    );

    try {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/webhotelier-api`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        return {
          data: null,
          error: new Error(`HTTP ${response.status}: ${errorText}`),
        };
      }

      const result = await response.json();
      return { data: result as T, error: null };
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof Error && err.name === 'AbortError') {
        return { data: null, error: new Error('Request timed out after 15s') };
      }
      return {
        data: null,
        error: err instanceof Error ? err : new Error('Unknown error'),
      };
    }
  }

  private async invokeEdgeFunction(body: Record<string, unknown>) {
    const { data, error } = await supabase.functions.invoke('webhotelier-api', {
      body,
    });
    return {
      data,
      error: error ? await resolveFunctionsHttpError(error) : null,
    };
  }

  private getAvailabilityCacheKey(request: AvailabilityRequest): string {
    return `${request.checkin}-${request.checkout}-${request.adults || 2}-${request.rooms || 1}`;
  }

  private isCacheValid(cacheTime: number): boolean {
    return Date.now() - cacheTime < WebHotelierProxyService.CACHE_TTL;
  }

  async testConnection(): Promise<ErrorHandlingResult<boolean>> {
    try {
      const result = await this.getPropertyInfo();
      return createSuccessResult(result.success);
    } catch (error) {
      return createErrorResult({
        code: ERROR_CODES.CONNECTION_FAILED,
        message:
          error instanceof Error ? error.message : 'Connection test failed',
        severity: 'high',
        retryable: true,
      });
    }
  }

  async getPropertyInfo(): Promise<ErrorHandlingResult<PropertyInfo>> {
    // Return cached property info if still valid
    if (
      this.propertyInfoCache &&
      this.isCacheValid(this.propertyInfoCacheTime)
    ) {
      return createSuccessResult(this.propertyInfoCache);
    }

    // If there's already a pending request, return it (deduplication)
    if (this.pendingPropertyInfo) {
      console.log('[WebHotelier Proxy] Reusing pending property info request');
      return this.pendingPropertyInfo;
    }

    // Create the actual request
    this.pendingPropertyInfo = this.fetchPropertyInfo();

    try {
      const result = await this.pendingPropertyInfo;
      return result;
    } finally {
      this.pendingPropertyInfo = null;
    }
  }

  private async fetchPropertyInfo(): Promise<
    ErrorHandlingResult<PropertyInfo>
  > {
    try {
      console.log(
        '[WebHotelier Proxy] Fetching property info via Edge Function',
        {
          propertyCode: this.config.propertyCode,
          endpoint: this.config.endpoint,
        }
      );

      // Use direct fetch instead of supabase.functions.invoke for better reliability
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const authHeaders = await getAuthHeaders();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await fetch(
          `${supabaseUrl}/functions/v1/webhotelier-api`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            body: JSON.stringify({
              action: 'getPropertyInfo',
              ...this.getRequestBody(),
            }),
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            '[WebHotelier Proxy] HTTP error:',
            response.status,
            errorText
          );
          return createErrorResult({
            code: ERROR_CODES.INTERNAL_ERROR,
            message: `HTTP ${response.status}: ${errorText}`,
            severity: 'high',
            retryable: true,
          });
        }

        const result = await response.json();
        const data = result;
        const error =
          result.success === false ? { message: result.error } : null;

        console.log('[WebHotelier Proxy] Edge Function response:', {
          hasData: !!data,
          hasError: !!error,
          success: data?.success,
        });

        if (error) {
          console.error('[WebHotelier Proxy] Edge Function error:', error);
          return createErrorResult({
            code: ERROR_CODES.INTERNAL_ERROR,
            message: error.message || 'Failed to fetch property info',
            severity: 'high',
            retryable: true,
          });
        }

        if (!data?.success) {
          console.error('[WebHotelier Proxy] API error:', data);
          return createErrorResult({
            code: ERROR_CODES.INTERNAL_ERROR,
            message: data?.error || 'Failed to fetch property from WebHotelier',
            severity: 'high',
            retryable: true,
          });
        }

        console.log(
          '[WebHotelier Proxy] Property info fetched successfully:',
          data.data?.data?.name || data.data?.name
        );
        const propertyData = data.data?.data || data.data;

        // Cache the result
        this.propertyInfoCache = propertyData;
        this.propertyInfoCacheTime = Date.now();

        return createSuccessResult(propertyData);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        const errorMessage =
          fetchError instanceof Error ? fetchError.message : 'Unknown error';
        console.error('[WebHotelier Proxy] Fetch error:', errorMessage);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: errorMessage.includes('aborted')
            ? 'Request timed out'
            : errorMessage,
          severity: 'high',
          retryable: true,
        });
      }
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getAvailability(
    request: AvailabilityRequest
  ): Promise<ErrorHandlingResult<AvailabilityResponse>> {
    try {
      // Check cache first
      const cacheKey = this.getAvailabilityCacheKey(request);
      const cached = this.availabilityCache.get(cacheKey);
      if (
        cached &&
        Date.now() - cached.time <
          WebHotelierProxyService.AVAILABILITY_CACHE_TTL
      ) {
        console.log('[WebHotelier Proxy] Returning cached availability', {
          checkin: request.checkin,
          checkout: request.checkout,
        });
        return createSuccessResult(cached.data);
      }

      console.log(
        '[WebHotelier Proxy] Fetching availability via Edge Function',
        {
          checkin: request.checkin,
          checkout: request.checkout,
          propertyCode: this.config.propertyCode,
          hasConfig: !!this.config,
        }
      );

      const { data, error } = await this.invokeWithTimeout('webhotelier-api', {
        action: 'getAvailability',
        ...this.getRequestBody(),
        checkin: request.checkin,
        checkout: request.checkout,
        adults: request.adults || 2,
        children: request.children || 0,
        rooms: request.rooms || 1,
        nights: request.nights,
        breakdown: request.breakdown || true,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch availability',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message:
            data?.error || 'Failed to fetch availability from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Availability fetched successfully');
      // WebHotelier API wraps response in { request_id, data: {...} }
      const availData = data.data?.data || data.data;

      // Cache the result
      this.availabilityCache.set(cacheKey, {
        data: availData,
        time: Date.now(),
      });

      // Clean up old cache entries (keep only last 50)
      if (this.availabilityCache.size > 50) {
        const entries = Array.from(this.availabilityCache.entries());
        entries.sort((a, b) => a[1].time - b[1].time);
        for (let i = 0; i < entries.length - 50; i++) {
          this.availabilityCache.delete(entries[i][0]);
        }
      }

      return createSuccessResult(availData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getBooking(
    bookingCode: string
  ): Promise<ErrorHandlingResult<BookingDetails>> {
    try {
      console.log('[WebHotelier Proxy] Fetching booking via Edge Function');

      const { data, error } = await this.invokeEdgeFunction({
        action: 'getBooking',
        ...this.getRequestBody(),
        bookingCode,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch booking',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to fetch booking from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Booking fetched successfully');
      const bookingData = data.data?.data || data.data;
      return createSuccessResult(bookingData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async searchBookings(
    filters: BookingSearchFilters
  ): Promise<ErrorHandlingResult<BookingSearchResult>> {
    try {
      console.log('[WebHotelier Proxy] Searching bookings via Edge Function');

      const { data, error } = await this.invokeWithTimeout('webhotelier-api', {
        action: 'searchBookings',
        ...this.getRequestBody(),
        filters,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to search bookings',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to search bookings from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Bookings searched successfully');
      const searchData = data.data?.data || data.data;
      return createSuccessResult(searchData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async cancelBooking(
    bookingCode: string,
    reason?: string
  ): Promise<ErrorHandlingResult<CancellationResponse>> {
    try {
      if (!this.propertyId) {
        return authorizationFailure(
          new PropertyAuthorizationError(
            'No active property set — cannot cancel booking.'
          )
        );
      }
      try {
        await assertPropertyAccess(this.propertyId);
      } catch (authError) {
        return authorizationFailure(authError);
      }

      console.log('[WebHotelier Proxy] Cancelling booking via Edge Function');

      const { data, error } = await this.invokeEdgeFunction({
        action: 'cancelBooking',
        ...this.getRequestBody(),
        bookingCode,
        reason,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to cancel booking',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to cancel booking in WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Booking cancelled successfully');
      const cancelData = data.data?.data || data.data;
      return createSuccessResult(cancelData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async purgeBooking(
    bookingCode: string
  ): Promise<ErrorHandlingResult<boolean>> {
    try {
      if (!this.propertyId) {
        return authorizationFailure(
          new PropertyAuthorizationError(
            'No active property set — cannot purge booking.'
          )
        );
      }
      try {
        await assertPropertyAccess(this.propertyId);
      } catch (authError) {
        return authorizationFailure(authError);
      }

      console.log('[WebHotelier Proxy] Purging booking via Edge Function');

      const { data, error } = await this.invokeEdgeFunction({
        action: 'purgeBooking',
        ...this.getRequestBody(),
        bookingCode,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to purge booking',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to purge booking in WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Booking purged successfully');
      return createSuccessResult(data.success);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async sendConfirmationEmail(
    bookingCode: string
  ): Promise<ErrorHandlingResult<boolean>> {
    try {
      console.log(
        '[WebHotelier Proxy] Sending confirmation email via Edge Function'
      );

      const { data, error } = await this.invokeEdgeFunction({
        action: 'sendConfirmationEmail',
        ...this.getRequestBody(),
        bookingCode,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to send confirmation email',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message:
            data?.error || 'Failed to send confirmation email via WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Confirmation email sent successfully');
      return createSuccessResult(data.success);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async pullBookings(
    since?: Date
  ): Promise<ErrorHandlingResult<BookingPullResponse>> {
    try {
      console.log('[WebHotelier Proxy] Pulling bookings via Edge Function');

      const { data, error } = await this.invokeWithTimeout('webhotelier-api', {
        action: 'pullBookings',
        ...this.getRequestBody(),
        since: since?.toISOString(),
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to pull bookings',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to pull bookings from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Bookings pulled successfully');
      const pullData = data.data?.data || data.data;

      // Debug: Log the full booking data structure to verify dates are present
      console.log('[WebHotelier Proxy] Pull bookings response:', {
        count: pullData?.bookings?.length || 0,
        sampleBookings: (pullData?.bookings || [])
          .slice(0, 2)
          .map((b: Record<string, unknown>) => ({
            id: b.booking_id || b.confirmation_number,
            checkin: b.checkin,
            checkout: b.checkout,
            room_type: b.room_type,
            status: b.status,
          })),
      });

      return createSuccessResult(pullData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getRoomTypes(): Promise<ErrorHandlingResult<RoomListResponse>> {
    // Return cached room types if still valid
    if (this.roomTypesCache && this.isCacheValid(this.roomTypesCacheTime)) {
      return createSuccessResult(this.roomTypesCache);
    }

    // If there's already a pending request, return it (deduplication)
    if (this.pendingRoomTypes) {
      console.log('[WebHotelier Proxy] Reusing pending room types request');
      return this.pendingRoomTypes;
    }

    // Create the actual request
    this.pendingRoomTypes = this.fetchRoomTypes();

    try {
      const result = await this.pendingRoomTypes;
      return result;
    } finally {
      this.pendingRoomTypes = null;
    }
  }

  private async fetchRoomTypes(): Promise<
    ErrorHandlingResult<RoomListResponse>
  > {
    try {
      console.log('[WebHotelier Proxy] Fetching room types via Edge Function');

      const { data, error } = await this.invokeWithTimeout('webhotelier-api', {
        action: 'getRoomTypes',
        ...this.getRequestBody(),
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch room types',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to fetch room types from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Room types fetched successfully');
      const roomsData = data.data?.data || data.data;

      // Cache the result
      this.roomTypesCache = roomsData;
      this.roomTypesCacheTime = Date.now();

      return createSuccessResult(roomsData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getRoomType(
    roomCode: string
  ): Promise<ErrorHandlingResult<RoomTypeInfo>> {
    try {
      console.log('[WebHotelier Proxy] Fetching room type via Edge Function');

      const { data, error } = await this.invokeEdgeFunction({
        action: 'getRoomType',
        ...this.getRequestBody(),
        roomCode,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch room type',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to fetch room type from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Room type fetched successfully');
      const roomTypeData = data.data?.data || data.data;
      return createSuccessResult(roomTypeData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getRates(): Promise<ErrorHandlingResult<RateListResponse>> {
    try {
      // Return cached rates if still valid
      if (this.ratesCache && this.isCacheValid(this.ratesCacheTime)) {
        return createSuccessResult(this.ratesCache);
      }

      console.log('[WebHotelier Proxy] Fetching rates via Edge Function');

      const { data, error } = await this.invokeWithTimeout('webhotelier-api', {
        action: 'getRates',
        ...this.getRequestBody(),
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch rates',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to fetch rates from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Rates fetched successfully');
      const ratesData = data.data?.data || data.data;

      // Cache the result
      this.ratesCache = ratesData;
      this.ratesCacheTime = Date.now();

      return createSuccessResult(ratesData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getRate(rateCode: string): Promise<ErrorHandlingResult<RateDetails>> {
    try {
      console.log('[WebHotelier Proxy] Fetching rate via Edge Function');

      const { data, error } = await this.invokeEdgeFunction({
        action: 'getRate',
        ...this.getRequestBody(),
        rateCode,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch rate',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'Failed to fetch rate from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Rate fetched successfully');
      const rateData = data.data?.data || data.data;
      return createSuccessResult(rateData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async updateAvailability(
    updates: AvailabilityUpdate
  ): Promise<ErrorHandlingResult<AvailabilityUpdateResponse>> {
    try {
      if (!this.propertyId) {
        return authorizationFailure(
          new PropertyAuthorizationError(
            'No active property set — cannot update availability.'
          )
        );
      }
      try {
        await assertPropertyAccess(this.propertyId);
      } catch (authError) {
        return authorizationFailure(authError);
      }

      console.log(
        '[WebHotelier Proxy] Updating availability via Edge Function'
      );

      const { data, error } = await this.invokeEdgeFunction({
        action: 'updateAvailability',
        ...this.getRequestBody(),
        updates,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to update availability',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message:
            data?.error || 'Failed to update availability in WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Availability updated successfully');
      const updateData = data.data?.data || data.data;
      return createSuccessResult(updateData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getAvailabilityCalendar(
    request: CalendarRequest
  ): Promise<ErrorHandlingResult<CalendarResponse>> {
    try {
      // WebHotelier calendar API only supports current month or later
      // Validate and adjust dates to prevent 400 errors
      const today = new Date();
      const currentMonthStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
      const currentMonthStartStr = currentMonthStart
        .toISOString()
        .split('T')[0];

      const requestFromDate = new Date(request.from);
      const requestToDate = new Date(request.to);

      // If both dates are in the past (before current month), return empty result
      if (
        requestFromDate < currentMonthStart &&
        requestToDate < currentMonthStart
      ) {
        console.log(
          '[WebHotelier Proxy] Calendar request dates are in the past, returning empty result'
        );
        return createSuccessResult({
          calendar: [],
          days: [],
        } as CalendarResponse);
      }

      // Adjust 'from' date if it's before current month
      const adjustedFrom =
        requestFromDate < currentMonthStart
          ? currentMonthStartStr
          : request.from;

      console.log(
        `[WebHotelier Proxy] Fetching availability calendar via Edge Function (${adjustedFrom} to ${request.to})`
      );

      const { data, error } = await this.invokeWithTimeout('webhotelier-api', {
        action: 'getAvailabilityCalendar',
        ...this.getRequestBody(),
        from: adjustedFrom,
        to: request.to,
        room: request.room,
        roomTypes: request.roomTypes,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch availability calendar',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message:
            data?.error ||
            'Failed to fetch availability calendar from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log(
        '[WebHotelier Proxy] Availability calendar fetched successfully'
      );
      const calendarData = data.data?.data || data.data;
      return createSuccessResult(calendarData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getAvailabilityFeed(
    from: string,
    to: string,
    roomCode?: string
  ): Promise<
    ErrorHandlingResult<
      Record<
        string,
        Array<{
          from?: string;
          to?: string;
          date?: string;
          allot: number;
          stopsell?: boolean;
          minstay?: number;
          maxstay?: number;
          checkin?: boolean;
          checkout?: boolean;
        }>
      >
    >
  > {
    try {
      console.log(
        '[WebHotelier Proxy] Fetching availability feed via Edge Function'
      );

      const { data, error } = await this.invokeEdgeFunction({
        action: 'getAvailabilityFeed',
        ...this.getRequestBody(),
        from,
        to,
        roomCode,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch availability feed',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message:
            data?.error || 'Failed to fetch availability feed from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Availability feed fetched successfully');
      const feedData = data.data?.data || data.data;
      return createSuccessResult(feedData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getAvailabilityBreakdown(
    request: AvailabilityRequest
  ): Promise<ErrorHandlingResult<BreakdownResponse>> {
    try {
      console.log(
        '[WebHotelier Proxy] Fetching availability breakdown via Edge Function'
      );

      const { data, error } = await this.invokeEdgeFunction({
        action: 'getAvailabilityBreakdown',
        ...this.getRequestBody(),
        checkin: request.checkin,
        checkout: request.checkout,
        adults: request.adults || 2,
        children: request.children || 0,
        rooms: request.rooms || 1,
        nights: request.nights,
        breakdown: true,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Failed to fetch availability breakdown',
          severity: 'high',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message:
            data?.error ||
            'Failed to fetch availability breakdown from WebHotelier',
          severity: 'high',
          retryable: true,
        });
      }

      console.log(
        '[WebHotelier Proxy] Availability breakdown fetched successfully'
      );
      const breakdownData = data.data?.data || data.data;
      return createSuccessResult(breakdownData);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  async getIntegrationStatus(): Promise<WebHotelierIntegrationStatus> {
    try {
      console.log('[WebHotelier Proxy] Checking integration status...');
      const connectionTest = await this.testConnection();
      console.log(
        '[WebHotelier Proxy] Connection test result:',
        connectionTest.success
      );

      return {
        connected: connectionTest.success,
        lastError: connectionTest.error
          ? {
              code: connectionTest.error.code,
              message: connectionTest.error.message,
              details: connectionTest.error.details,
            }
          : undefined,
        apiCallsToday: 0,
        rateLimitRemaining: 1000,
        health: connectionTest.success ? 'healthy' : 'error',
        version: '1.0.0',
      };
    } catch (error) {
      console.error(
        '[WebHotelier Proxy] Integration status check failed:',
        error
      );
      return {
        connected: false,
        lastError: {
          code: 'CONNECTION_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
        apiCallsToday: 0,
        rateLimitRemaining: 0,
        health: 'error',
        version: '1.0.0',
      };
    }
  }

  async updateRates(
    rateUpdates: RateUpdateRequest
  ): Promise<ErrorHandlingResult<RateUpdateResponse>> {
    try {
      if (!this.propertyId) {
        return authorizationFailure(
          new PropertyAuthorizationError(
            'No active property set — cannot update rates.'
          )
        );
      }
      try {
        await assertPropertyAccess(this.propertyId);
      } catch (authError) {
        return authorizationFailure(authError);
      }

      const totalUpdates = Object.values(rateUpdates).reduce(
        (sum, updates) => sum + updates.length,
        0
      );

      console.log('[WebHotelier Proxy] Updating rates via Edge Function', {
        rateCodes: Object.keys(rateUpdates),
        totalUpdates,
      });

      // Transform RateUpdateRequest to the WebHotelier API format
      // WebHotelier expects: { "[RATECODE]": [ { from, to, pricing: [...], ... } ] }
      // IMPORTANT: WebHotelier requires "equal payloads must be packed" -
      // consecutive dates with the same price must be sent as date ranges
      const batchPayload: Record<
        string,
        Array<{
          date?: string;
          from?: string;
          to?: string;
          pricing: Array<{ adults: number; price: number }>;
          min_stay?: number;
          max_stay?: number;
          closeout?: boolean;
        }>
      > = {};

      for (const [rateCode, updates] of Object.entries(rateUpdates)) {
        // Collect all date-based updates with their pricing
        const dateUpdates: Array<{
          date: string;
          price: number;
          min_los?: number;
          max_los?: number;
          closeout?: boolean;
        }> = [];

        for (const update of updates) {
          // Get price from pricing array (default to 2 adults)
          const price =
            update.pricing?.find((p) => p.adults === 2)?.price ||
            update.pricing?.[0]?.price ||
            0;

          if (price <= 0) continue;

          if (update.date) {
            dateUpdates.push({
              date: update.date,
              price,
              min_los: update.min_los,
              max_los: update.max_los,
              closeout: update.closeout,
            });
          } else if (update.period) {
            // Period updates are already ranges, add them directly
            if (!batchPayload[rateCode]) {
              batchPayload[rateCode] = [];
            }
            const pricingArray = [
              { adults: 1, price: Math.round(price * 0.9 * 100) / 100 },
              { adults: 2, price },
            ];
            batchPayload[rateCode].push({
              from: update.period.from,
              to: update.period.to,
              pricing: pricingArray,
              ...(update.min_los && { min_stay: update.min_los }),
              ...(update.max_los && { max_stay: update.max_los }),
              ...(update.closeout !== undefined && {
                closeout: update.closeout,
              }),
            });
          }
        }

        // Sort date updates by date
        dateUpdates.sort((a, b) => a.date.localeCompare(b.date));

        // Pack consecutive dates with the same price into ranges
        const packedRanges: Array<{
          from: string;
          to: string;
          price: number;
          min_los?: number;
          max_los?: number;
          closeout?: boolean;
        }> = [];

        for (const update of dateUpdates) {
          const lastRange = packedRanges[packedRanges.length - 1];

          // Check if this date can be added to the current range
          // Price AND min_los must match for packing (different min_los = different payload)
          if (
            lastRange &&
            lastRange.price === update.price &&
            (lastRange.min_los || 0) === (update.min_los || 0)
          ) {
            // Check if dates are consecutive
            const lastDate = new Date(lastRange.to);
            const currentDate = new Date(update.date);
            const diffDays =
              (currentDate.getTime() - lastDate.getTime()) /
              (1000 * 60 * 60 * 24);

            if (diffDays === 1) {
              // Extend the range
              lastRange.to = update.date;
              continue;
            }
          }

          // Start a new range
          packedRanges.push({
            from: update.date,
            to: update.date,
            price: update.price,
            min_los: update.min_los,
            max_los: update.max_los,
            closeout: update.closeout,
          });
        }

        // Convert packed ranges to WebHotelier format
        if (!batchPayload[rateCode]) {
          batchPayload[rateCode] = [];
        }

        for (const range of packedRanges) {
          // WebHotelier requires pricing for ALL occupancy levels starting from minimum
          const pricingArray = [
            { adults: 1, price: Math.round(range.price * 0.9 * 100) / 100 },
            { adults: 2, price: range.price },
          ];

          batchPayload[rateCode].push({
            from: range.from,
            to: range.to,
            pricing: pricingArray,
            ...(range.min_los && { min_stay: range.min_los }),
            ...(range.max_los && { max_stay: range.max_los }),
            ...(range.closeout !== undefined && { closeout: range.closeout }),
          });
        }
      }

      // Send all updates in a SINGLE API call
      console.log(
        '[WebHotelier Proxy] Sending batch update with',
        Object.keys(batchPayload).length,
        'rate codes:',
        Object.keys(batchPayload)
      );
      console.log(
        '[WebHotelier Proxy] Packed ranges count:',
        Object.entries(batchPayload).map(
          ([code, ranges]) => `${code}: ${ranges.length} ranges`
        )
      );

      const { data, error } = await this.invokeEdgeFunction({
        action: 'updateRates',
        ...this.getRequestBody(),
        rateUpdates: batchPayload,
      });

      const errors: Array<{ rate_code: string; date: string; error: string }> =
        [];

      if (error) {
        console.error('[WebHotelier Proxy] Edge Function error:', error);
        // Mark all updates as failed
        for (const [rateCode, updates] of Object.entries(batchPayload)) {
          for (const update of updates) {
            errors.push({
              rate_code: rateCode,
              date: update.date || update.from || 'unknown',
              error: error.message || 'Failed to update rate',
            });
          }
        }
      } else if (!data?.success) {
        console.error('[WebHotelier Proxy] API error:', data);
        // WebHotelier API error - all updates in batch failed together
        for (const [rateCode, updates] of Object.entries(batchPayload)) {
          for (const update of updates) {
            errors.push({
              rate_code: rateCode,
              date: update.date || update.from || 'unknown',
              error: data?.error || 'Failed to update rate in WebHotelier',
            });
          }
        }
      }

      const response: RateUpdateResponse = {
        success: errors.length === 0,
        errors: errors.length > 0 ? errors : undefined,
        updated:
          errors.length === 0
            ? Object.keys(rateUpdates).map((rateCode) => ({
                rate_code: rateCode,
                dates_updated: rateUpdates[rateCode]?.length || 0,
                pricing_updated: true,
              }))
            : undefined,
      };

      if (errors.length > 0) {
        console.warn(
          `[WebHotelier Proxy] Rate update failed with ${errors.length} errors`
        );
      } else {
        console.log(
          `[WebHotelier Proxy] Successfully updated ${totalUpdates} rate entries in a single batch`
        );
      }

      return createSuccessResult(response);
    } catch (error) {
      console.error('[WebHotelier Proxy] Error updating rates:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }

  /**
   * Push restriction-only updates (min_stay, max_stay) to specific rate IDs.
   * Used for child/derived rates (e.g. BAR) where pricing modifications are rejected.
   * Sends directly to WebHotelier without any pricing data.
   */
  async updateRestrictions(
    updates: Array<{
      rateId: string;
      from: string;
      to: string;
      minStay?: number;
      maxStay?: number;
    }>
  ): Promise<ErrorHandlingResult<{ success: boolean }>> {
    try {
      if (updates.length === 0) {
        return createSuccessResult({ success: true });
      }

      if (!this.propertyId) {
        return authorizationFailure(
          new PropertyAuthorizationError(
            'No active property set — cannot update restrictions.'
          )
        );
      }
      try {
        await assertPropertyAccess(this.propertyId);
      } catch (authError) {
        return authorizationFailure(authError);
      }

      // Group by rate ID and pack consecutive dates with same restrictions into ranges
      const byRate = new Map<
        string,
        Array<{ from: string; to: string; minStay?: number; maxStay?: number }>
      >();
      for (const u of updates) {
        if (!byRate.has(u.rateId)) byRate.set(u.rateId, []);
        byRate.get(u.rateId)!.push(u);
      }

      // Build payload: { "rateId": [ { from, to, min_stay } ] }
      const payload: Record<
        string,
        Array<{
          from: string;
          to: string;
          min_stay?: number;
          max_stay?: number;
        }>
      > = {};

      for (const [rateId, entries] of byRate) {
        // Sort by date
        entries.sort((a, b) => a.from.localeCompare(b.from));

        // Pack consecutive dates with same restrictions into ranges
        const packed: typeof entries = [];
        for (const entry of entries) {
          const last = packed[packed.length - 1];
          if (
            last &&
            (last.minStay || 0) === (entry.minStay || 0) &&
            (last.maxStay || 0) === (entry.maxStay || 0)
          ) {
            const lastDate = new Date(last.to);
            const currDate = new Date(entry.from);
            const diffDays =
              (currDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24);
            if (diffDays === 1) {
              last.to = entry.to;
              continue;
            }
          }
          packed.push({ ...entry });
        }

        payload[rateId] = packed.map((e) => ({
          from: e.from,
          to: e.to,
          ...(e.minStay != null && { min_stay: e.minStay }),
          ...(e.maxStay != null && { max_stay: e.maxStay }),
        }));
      }

      console.log(
        `[WebHotelier Proxy] Pushing restriction-only updates to ${Object.keys(payload).length} child rate(s):`,
        Object.entries(payload).map(([id, entries]) => ({
          rateId: id,
          entries: entries.length,
        }))
      );

      const { data, error } = await this.invokeEdgeFunction({
        action: 'updateRates',
        ...this.getRequestBody(),
        rateUpdates: payload,
      });

      if (error) {
        console.error('[WebHotelier Proxy] Restriction update error:', error);
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: error.message || 'Restriction update failed',
          severity: 'medium',
          retryable: true,
        });
      }

      if (!data?.success) {
        console.error(
          '[WebHotelier Proxy] Restriction update API error:',
          data
        );
        return createErrorResult({
          code: ERROR_CODES.INTERNAL_ERROR,
          message: data?.error || 'WebHotelier rejected restriction update',
          severity: 'medium',
          retryable: true,
        });
      }

      console.log('[WebHotelier Proxy] Restriction update successful');
      return createSuccessResult({ success: true });
    } catch (error) {
      console.error('[WebHotelier Proxy] Error updating restrictions:', error);
      return createErrorResult({
        code: ERROR_CODES.INTERNAL_ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'high',
        retryable: false,
      });
    }
  }
}

// --- Per-property singleton management ---

let activePropertyId: string | null = null;
let activePropertyCode: string | null = null;
const instanceCache = new Map<string, WebHotelierProxyService>();

// Downstream singleton reset functions — populated by registerResetHandler
const resetHandlers: Array<() => void> = [];

export function registerResetHandler(handler: () => void): void {
  resetHandlers.push(handler);
}

export function setActiveProperty(id: string, code: string): void {
  if (activePropertyId === id) return;
  console.log(
    `[WebHotelier Proxy Service] Switching active property: ${activePropertyId} → ${id} (${code})`
  );
  activePropertyId = id;
  activePropertyCode = code;

  // Reset downstream singletons so they pick up the new property's proxy instance
  for (const reset of resetHandlers) {
    reset();
  }
}

export function getWebHotelierProxyService(): WebHotelierProxyService | null {
  try {
    // If an active property is set, return/create a propertyId-based instance
    if (activePropertyId) {
      const cached = instanceCache.get(activePropertyId);
      if (cached) return cached;

      console.log(
        `[WebHotelier Proxy Service] Creating instance for property ${activePropertyId} (${activePropertyCode})`
      );
      // Minimal config — Edge Function handles credential lookup via propertyId
      const config: WebHotelierConfig = {
        endpoint: 'https://rest.reserve-online.net',
        username: '',
        password: '',
        propertyCode: activePropertyCode || '',
        timeout: 30000,
        retryAttempts: 3,
        rateLimitDelay: 1000,
      };
      const instance = new WebHotelierProxyService(config, activePropertyId);
      instanceCache.set(activePropertyId, instance);
      return instance;
    }

    // No active property — nothing to do. We used to fall back to
    // VITE_WEBHOTELIER_* env vars here, but Vite inlines those into the
    // shipped JS bundle (same class as Docker build-arg leakage), so any
    // credentials placed there would be readable by any browser user.
    // Credentials are now only resolved server-side from pms_integrations
    // keyed by the active propertyId.
    console.warn(
      '[WebHotelier Proxy Service] No active property set — cannot initialize'
    );
    return null;
  } catch (error) {
    console.error(
      '[WebHotelier Proxy Service] Fatal error during initialization:',
      error
    );
    return null;
  }
}
